import type { CustomVariable, VariableBucket, VariableCondition } from './crosstab-types';
import { bucketGroups, conditionComplete, groupJoinOf, isGrouped } from '@/lib/crosstab/conditions';

/** True when any response option is claimed by more than one net group. */
export function netsOverlap(groups: { optionIds: string[] }[]): boolean {
    const seen = new Set<string>();
    for (const g of groups) {
        for (const id of g.optionIds) {
            if (seen.has(id)) return true;
            seen.add(id);
        }
    }
    return false;
}

export interface VariableSaveState {
    name: string;
    mode: 'logic' | 'bucketing' | 'formula' | 'combine' | 'nets';
    placement?: 'row' | 'banner';
    buckets: VariableBucket[];
    sourceQuestionId?: string;
    isCategoricalSource?: boolean;
    catGroups: { label: string; optionIds: string[] }[];
    ranges: { label: string }[];
    rangeOverlaps?: boolean;
    formulaItems: { questionId: string; coefficient: number }[];
    combineFields: { questionId: string }[];
    netsSourceQid?: string;
    netsGroups: { label: string; optionIds: string[] }[];
}

/**
 * The single unmet requirement blocking "Create Variable", as text a researcher can act on — or
 * `null` when the variable is savable.
 *
 * BT-2018: this used to be a bare boolean `canSave`, whose first term was `!name.trim()`. Opened
 * from the row pencil the name starts empty, and the modal body scrolls the name field out of view
 * while the footer button stays pinned — so selecting stubs never enabled the button and there was
 * nothing on screen saying why. Returning the reason, and rendering it next to the button, is the
 * fix; the enablement rule itself is unchanged.
 */
export function variableSaveBlocker(s: VariableSaveState): string | null {
    if (!s.name.trim()) return 'Enter a variable name';

    if (s.mode === 'logic') {
        if (s.buckets.length === 0) return 'Add at least one bucket';
        if (!s.buckets.every((b) => b.label.trim().length > 0)) return 'Name every bucket';
        // M4S-5777 (X46): any of / none of need an answer, a range an end (`conditionComplete`).
        if (!s.buckets.every((b) => b.conditions.every(conditionComplete))) {
            return 'Every condition needs a question and a value';
        }
        return null;
    }

    if (s.mode === 'bucketing') {
        if (!s.sourceQuestionId) return 'Select a source question';
        if (s.isCategoricalSource) {
            return s.catGroups.some((g) => g.label.trim() && g.optionIds.length > 0)
                ? null
                : 'Name a group and select at least one option';
        }
        if (s.rangeOverlaps) return 'Ranges overlap — adjust the boundaries';
        if (s.ranges.length === 0) return 'Add at least one range';
        if (!s.ranges.every((r) => r.label.trim().length > 0)) return 'Name every range';
        return null;
    }

    if (s.mode === 'formula') {
        if (s.formulaItems.length < 2) return 'Add at least two questions';
        return s.formulaItems.every((i) => i.questionId.trim() && !isNaN(i.coefficient))
            ? null
            : 'Every term needs a question and a numeric coefficient';
    }

    if (s.mode === 'combine') {
        if (s.combineFields.length < 2) return 'Add at least two questions';
        return s.combineFields.every((f) => f.questionId.trim().length > 0)
            ? null
            : 'Select a question for every field';
    }

    if (s.mode === 'nets') {
        if (!s.netsSourceQid) return 'Select a source question';
        const filled = s.netsGroups.filter((g) => g.label.trim() && g.optionIds.length > 0);
        if (filled.length === 0) return 'Name each net and select at least one option';
        const labels = filled.map((g) => g.label.trim().toLowerCase());
        if (labels.length !== new Set(labels).size) return 'Two nets share the same name';
        // BT-2019: nets may overlap freely on rows, but overlapping banner columns share respondents,
        // which breaks the independence both the chi-square and the two-proportion z-test assume.
        if (s.placement === 'banner' && netsOverlap(filled)) {
            return "Overlapping nets can't be banner columns — significance testing needs columns that don't share respondents. Place this on rows, or remove the overlap.";
        }
        return null;
    }

    return 'Choose a variable type';
}

/** Returns true when a bucket uses AND join and has two or more `eq` conditions
 *  pointing at the same questionId. A single-select respondent can only hold one
 *  answer, so this combination always yields a 0 count. */
export function hasDuplicateEqOnSameQuestion(bucket: VariableBucket): boolean {
    // M4S-5777 (X46): in a grouped bucket, each AND group is checked on its own.
    const andLists = isGrouped(bucket)
        ? (bucketGroups(bucket) ?? []).filter((g) => g.join === 'all').map((g) => g.conditions)
        : bucket.join === 'all'
          ? [bucket.conditions]
          : [];
    return andLists.some((conditions) => {
        const eqQids = conditions
            .filter((c) => c.operator === 'eq' && c.questionId)
            .map((c) => c.questionId);
        return eqQids.length > new Set(eqQids).size;
    });
}

/**
 * The home question of a Logic variable: the one question every condition in every bucket names,
 * or '' when they span several. Set on create so the variable renders inside that question's block
 * (computeFromApi injection) instead of as a block of its own — Dennis, 2026-09-15: "this custom
 * option stub should live within that same question table, not outside of it."
 */
export function deriveSourceQuestionId(buckets: VariableBucket[]): string {
    let found = '';
    for (const b of buckets) {
        for (const c of b.conditions) {
            const q = c.questionId.trim();
            if (!q) continue;
            if (!found) found = q;
            else if (found.toUpperCase() !== q.toUpperCase()) return '';
        }
    }
    return found;
}

/**
 * True when the Nets tab can show this variable without losing anything: every condition is an
 * `eq` on the source question and every connector is OR. The Nets editor keeps only condition
 * values, so opening a variable with `neq`, a numeric operator or an AND there — and saving —
 * would silently rewrite it as a plain net. Decides which tab an existing variable opens on.
 */
export function isNetsRepresentable(cv: CustomVariable): boolean {
    if (cv.kind !== 'logic' || !cv.sourceQuestionId?.trim() || cv.buckets.length === 0) {
        return false;
    }
    if (cv.bucketJoin === 'all' || cv.buckets.some((b) => b.nextJoin === 'all')) return false;
    // M4S-5777 (X46): grouped buckets keep their groups only in Logic.
    if (cv.buckets.some(isGrouped)) return false;
    const src = cv.sourceQuestionId.trim().toUpperCase();
    return cv.buckets.every(
        (b) =>
            b.conditions.length > 0 &&
            b.conditions.every(
                (c, i) =>
                    c.operator === 'eq' &&
                    c.questionId.trim().toUpperCase() === src &&
                    (i === b.conditions.length - 1 || (c.nextJoin ?? b.join) === 'any'),
            ),
    );
}

/**
 * M4S-5605. The connector `evalBucket` uses after each condition but the last: the condition's own
 * `nextJoin`, else the bucket's `join`. A `nextJoin` on the last condition is never read.
 */
export function bucketConnectors(bucket: VariableBucket): ('all' | 'any')[] {
    return bucket.conditions.slice(0, -1).map((c) => c.nextJoin ?? bucket.join);
}

/** True when a bucket's connectors are not all the same, so no single OR / AND describes it. */
export function isMixedBucket(bucket: VariableBucket): boolean {
    const connectors = bucketConnectors(bucket);
    return connectors.some((c) => c !== connectors[0]);
}

/**
 * M4S-5605. What a bucket counts, as the sentence the Logic tab shows under it ("Reads as").
 *
 * `evalBucket` folds the conditions left to right with no precedence, so `A OR B AND C` means
 * `(A OR B) AND C`. This walks the same fold and adds parentheses wherever the operator changes:
 * `A OR B AND C OR D` reads `((A OR B) AND C) OR D`, while a run of one operator reads plain. A
 * reader who evaluates the sentence with ordinary rules gets exactly what compute counts, which
 * cv-utils-reads-as.test.ts checks for every connector sequence up to six conditions.
 *
 * `labelOf` names one condition (for example `AGE = 18 - 24`).
 */
export function describeBucket(
    bucket: VariableBucket,
    labelOf: (condition: VariableCondition) => string,
): string {
    const conditions = bucket.conditions;
    if (conditions.length === 0) return '';
    // M4S-5777 (X46): a grouped bucket reads as its groups, each in parentheses when it holds more
    // than one condition or is negated: (A OR B) AND NOT (C).
    const groups = bucketGroups(bucket);
    if (groups) {
        const parts = groups.map((g) => {
            const inner = g.conditions.map(labelOf).join(g.join === 'all' ? ' AND ' : ' OR ');
            const wrapped = g.conditions.length > 1 || g.not ? `(${inner})` : inner;
            return g.not ? `NOT ${wrapped}` : wrapped;
        });
        return parts.join(groupJoinOf(bucket) === 'all' ? ' AND ' : ' OR ');
    }
    const connectors = bucketConnectors(bucket);
    let text = labelOf(conditions[0]);
    let current: 'all' | 'any' | null = null;
    for (let i = 1; i < conditions.length; i++) {
        const op = connectors[i - 1];
        if (current !== null && op !== current) text = `(${text})`;
        text = `${text} ${op === 'all' ? 'AND' : 'OR'} ${labelOf(conditions[i])}`;
        current = op;
    }
    return text;
}

/**
 * M4S-5605. Whether compute reads an AND BETWEEN this variable's buckets, which would intersect
 * their columns. It mirrors compute's inter-bucket test (`isAnyInterBucketAnd` in
 * `buildCustomVarRows` and the chain in `addCustomVarCols`): a logic variable with more than one
 * distinct bucket label, where compute reads the per-bucket `nextJoin`s as soon as ANY bucket has
 * one, and `bucketJoin` only otherwise.
 *
 * It over-approximates on purpose, because it mirrors the test and not whether a chain forms. Two
 * shapes return true with no chain: an AND written only on a later bucket that shares its label
 * with an earlier one (compute reads a label's connector from its first bucket), and an AND
 * written only on the last bucket. Those variables keep the control and compute exactly as saved.
 */
export function usesBetweenBucketAnd(cv: CustomVariable): boolean {
    if (cv.kind !== 'logic') return false;
    if (new Set(cv.buckets.map((b) => b.label)).size < 2) return false;
    const hasPerBucketJoin = cv.buckets.some((b) => b.nextJoin !== undefined);
    return hasPerBucketJoin
        ? cv.buckets.some((b) => b.nextJoin === 'all')
        : cv.bucketJoin === 'all';
}

/**
 * M4S-5777 (X34): whether saving `after` over `before` changes no number, so it may show at once
 * instead of waiting for Run. True only when the two differ in nothing but the variable's name,
 * group, Uncategorized label, stored Nest recipe (compute never reads it), stored placement or its
 * project-variable stamp (`library`, X55),
 * AND the header its columns sit under is unchanged: compute merges custom variables into one
 * banner header by name (`headerOf`), and a header that joins another changes which columns are
 * compared, so the letters move. A change of id, conditions, buckets (their labels too: same-label
 * buckets are one column), ranges, base or rules is not a rename.
 */
export function isRenameOnly(before: CustomVariable, after: CustomVariable): boolean {
    const header = (cv: CustomVariable) => (cv.cvGroupName ?? cv.name).trim();
    if (header(before) !== header(after)) return false;
    const numbersOf = (cv: CustomVariable) => {
        const rest: Partial<CustomVariable> = { ...cv };
        delete rest.name;
        delete rest.cvGroupName;
        delete rest.otherLabel;
        delete rest.nest;
        delete rest.placement;
        // M4S-5777 (X55): which project variable it was copied from; compute never reads it.
        delete rest.library;
        return canonicalJson(rest);
    };
    return numbersOf(before) === numbersOf(after);
}

/**
 * M4S-5777 (X34): `target` with the names of `source`: its name, group and Uncategorized label.
 * What a rename may carry into the table at once; nothing that changes a number.
 */
export function withNamesOf(target: CustomVariable, source: CustomVariable): CustomVariable {
    const out: CustomVariable = { ...target, name: source.name, otherLabel: source.otherLabel };
    if (source.cvGroupName === undefined) delete out.cvGroupName;
    else out.cvGroupName = source.cvGroupName;
    return out;
}

/** JSON with every object's keys sorted, at every depth, and undefined fields left out. */
function canonicalJson(value: unknown): string {
    return JSON.stringify(value, (_key, v: unknown) =>
        v && typeof v === 'object' && !Array.isArray(v)
            ? Object.fromEntries(
                  Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
                      a < b ? -1 : a > b ? 1 : 0,
                  ),
              )
            : v,
    );
}
